import React, { useState, useCallback, useEffect, useRef, Fragment, memo } from 'react'
import PropTypes from 'prop-types'
import useEmblaCarousel from 'embla-carousel-react'
import CustomLeftArrow from '../customLeftArrow/customLeftArrow'
import { useRecursiveTimeout } from './useRecursiveTimeout'
import CustomRightArrow from '../customRightArrow/customRightArrow'
import { translate } from '../../helpers/stringHelpers'
import LazyImage from '../lazyImage'
import ZoomImage from '../zoomImage'

const thumbConfig = {
  containScroll: 'keepSnaps',
  dragFree: true
}

const carouselConfig = {
  align: 'start',
  containScroll: 'trimSnaps',
  speed: 40
}

const Carousel = ({
  id,
  autoplayInterval,
  children,
  childThumbs,
  childThumbsCountContent,
  className,
  hasThumbs,
  hasZoom,
  images,
  onSlide,
  showSlideCount,
  slidesToDisplay
}) => {
  const imageLength = images?.length || 0
  const childLength = children?.length || 0

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaRef, embla] = useEmblaCarousel(carouselConfig)
  const viewportRef = useRef(null)
  const [thumbViewportRef, emblaThumbs] = useEmblaCarousel(thumbConfig)

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false)

  const autoplay = useCallback(() => {
    if (!embla) return
    if (embla.canScrollNext()) {
      embla.scrollNext()
    } else {
      embla.scrollTo(0)
    }
  }, [embla])

  const { play, stop } = useRecursiveTimeout(autoplay, autoplayInterval)

  const onThumbClick = useCallback(
    (index) => {
      if (!embla || !hasThumbs) return
      if (embla.clickAllowed()) {
        embla.scrollTo(index)
        stop()
      }
    },
    [embla, stop, hasThumbs]
  )

  const onSelect = useCallback(() => {
    if (!embla) return
    setPrevBtnEnabled(embla.canScrollPrev())
    setNextBtnEnabled(embla.canScrollNext())
    setSelectedIndex(embla.selectedScrollSnap())
    if (hasThumbs && imageLength > 1 && emblaThumbs) {
      emblaThumbs.scrollTo(embla.selectedScrollSnap())
    }
  }, [embla, emblaThumbs, setSelectedIndex, hasThumbs, imageLength])

  const scrollPrev = useCallback(() => {
    if (!embla) return
    embla.scrollPrev()
    stop()
  }, [embla, stop])

  const scrollNext = useCallback(() => {
    if (!embla) return
    embla.scrollNext()
    stop()
  }, [embla, stop])

  useEffect(() => {
    if (!embla) return
    onSelect()
    embla.on('select', onSelect)
    embla.on('pointerDown', stop)
    if (onSlide) embla.on('settle', () => onSlide(embla.selectedScrollSnap()))
    // Ensure measurements match final layout after mount
    setTimeout(() => {
      if (embla) embla.reInit()
    }, 0)
  }, [embla, onSelect, stop, onSlide])

  // Recalculate when image set length changes
  useEffect(() => {
    if (embla) embla.reInit()
  }, [embla, imageLength])

  // Recalculate when the viewport element resizes (e.g., video-centric layout constrains width)
  useEffect(() => {
    if (!viewportRef.current || !embla || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => embla.reInit())
    ro.observe(viewportRef.current)
    return () => ro.disconnect()
  }, [embla])

  useEffect(() => {
    if (autoplayInterval) {
      play()
    }
  }, [play, autoplayInterval])

  if (!imageLength && !childLength) return null
  const hasArrows = imageLength + childLength > slidesToDisplay
  return (
    <Fragment key='carousel-fragment'>
      {[<div id={id} key='carousel' className={`carousel carousel--primary ${className}`}>
      {hasArrows ? (<CustomLeftArrow onClick={scrollPrev} key='carousel__arrow-left' disabled={!prevBtnEnabled} />) : null}
      <div
        className='embla'
        ref={(el) => {
          emblaRef(el)
          viewportRef.current = el
        }}
      >
        <div className='embla__container carousel__container'>
          {children
            ? children.filter(child => !!child).map((child, index) => (
              <div key={`carouselSlideChild${index}`} className={`embla__slide carousel__slide ${index === selectedIndex ? 'carousel__slide--active' : ''}`}>
                {child}
              </div>
            ))
            : null}
          {images.filter(image => !!image).map((image, index) => index <= selectedIndex + (slidesToDisplay * 2)
            ? (
              <div key={`carouselSlide${index}`} className={`embla__slide carousel__slide ${index + childLength === selectedIndex ? 'carousel__slide--active' : ''}`}>
                {hasZoom
                  ? (
                    <ZoomImage
                      label={image.label}
                      src={image.src}
                      fullscreenOnMobile
                      mobileBreakpoint={767}
                      key={`image--full-${image.src}`}
                    />
                    )
                  : (
                    <LazyImage
                      dataRef={image.dataRef}
                      label={image.label}
                      src={image.src}
                      onClick={image.onClick}
                      to={image.to}
                      key={`image-${image.src}`}
                    />
                    )}
              </div>
              )
            : <div key={`carouselSlide${index}`} className={`embla__slide carousel__slide ${index + childLength === selectedIndex ? 'carousel__slide--active' : ''}`} />)}
        </div>
      </div>
      {hasArrows ? (<CustomRightArrow onClick={scrollNext} disabled={!nextBtnEnabled} />) : null}
    </div>,
      showSlideCount && hasArrows
        ? (
        <div key='slideCount' className='text-muted text-center carousel__image-count'>
          <small>
            {childThumbs.length
              ? translate(childThumbsCountContent).replace('{0}', childThumbs.length)
              : null}
            {childThumbs.length && imageLength
              ? ' / '
              : null}
            {imageLength
              ? translate('JspPublicCodes_JSP_LISTING_IMAGE_COUNT').replace('{0}', imageLength)
              : null}
          </small>
        </div>
          )
        : null,
      hasThumbs && (childThumbs.length + imageLength > 1)
        ? (
        <div key='thumbs' className='embla embla--thumb'>
          <div className='embla__viewport' ref={thumbViewportRef}>
            <div className='embla__container embla__container--thumb'>
              {childThumbs.length
                ? childThumbs.filter(child => !!child).map((child, index) => (
                  <div key={`thumbnail-video-${index}`} className={`carousel__slide ${(index) === selectedIndex ? 'carousel__slide--active' : ''}`}>
                    {React.isValidElement(child)
                      ? React.cloneElement(child, {
                        onClick: () => onThumbClick(index)
                      })
                      : child
                    }
                  </div>
                ))
                : null}
              {images.map((image, index) => {
                const isActive = (index + children.length) === selectedIndex
                return (
                  <div key={`thumbnail-${image.src}`} className={`carousel__slide ${isActive ? 'carousel__slide--active' : ''}`}>
                    <LazyImage
                      key={`thumbnail-lazy-${image.src}`}
                      className={`thumbnail-${image.src} ${isActive ? `thumbnail-${image.src}--active` : ''}`}
                      identifier={index + children.length}
                      label={image.label}
                      src={image.src}
                      onClick={() => onThumbClick(index + childLength)}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </div>
          )
        : null
      ]}
    </Fragment>
  )
}

Carousel.propTypes = {
  autoplayInterval: PropTypes.number.isRequired,
  childThumbs: PropTypes.node,
  childThumbsCountContent: PropTypes.string,
  id: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.shape({
    dataRef: PropTypes.string,
    label: PropTypes.string,
    onClick: PropTypes.func,
    src: PropTypes.string.isRequired,
    to: PropTypes.string
  })),
  children: PropTypes.node,
  className: PropTypes.string,
  hasThumbs: PropTypes.bool.isRequired,
  hasZoom: PropTypes.bool.isRequired,
  onSlide: PropTypes.func,
  showSlideCount: PropTypes.bool,
  slidesToDisplay: PropTypes.number
}

Carousel.defaultProps = {
  autoplayInterval: 0,
  children: [],
  childThumbs: [],
  childThumbsCountContent: 'JspPublicCodes_JSP_LISTING_VIDEO_COUNT',
  className: '',
  hasThumbs: false,
  hasZoom: false,
  images: [],
  showSlideCount: false,
  slidesToDisplay: 1
}

const isEqual = (a, b) => a.images.length === b.images.length &&
  !!a.images[0]?.onClick === !!b.images[0]?.onClick

export default memo(Carousel, isEqual)
